import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import type { Device } from "../device/device";
import { t } from "../locale";
import {
	sortedSetAdd,
	sortedSetDelete,
	sortedSetIncludes,
} from "../sorted_set";
import { state } from "../state";
import { materialSymbol } from "./material-symbol";
import { button, div, input, label, span } from "./van-tags";

interface Shared {
	device: Device;
}

export const deviceTagsField = (shared: Shared): ChildDom => {
	const local = reactive({
		allTags: state.getDeviceTags(),
		newTag: "",
	});

	const onClickAdd = (): void => {
		shared.device.Metadata.Tags = sortedSetAdd(
			shared.device.Metadata.Tags,
			local.newTag,
		);
		local.allTags = sortedSetAdd(local.allTags, local.newTag);
		local.newTag = "";
	};

	const onInputNewTag = (e: Event): void => {
		if (!(e.target instanceof HTMLInputElement)) return;
		local.newTag = e.target.value;
	};

	return div(
		{ class: "field" },
		label({ class: "label" }, () => t.tags),
		div(
			{ class: "field has-addons" },
			div(
				{ class: "control is-expanded" },
				input({
					class: "input",
					oninput: onInputNewTag,
					type: "text",
					value: () => local.newTag,
				}),
			),
			div(
				{ class: "control" },
				button(
					{
						class: "button is-primary",
						onclick: onClickAdd,
					},
					() => t.add,
				),
			),
		),
		() =>
			div(
				{ class: "field is-grouped is-grouped-multiline" },
				local.allTags
					.filter((tag) => !isReservedTag(tag))
					.map((tag) =>
						sortedSetIncludes(shared.device.Metadata.Tags, tag)
							? selectedDeviceTagControl(shared, tag)
							: unselectedDeviceTagControl(shared, tag),
					),
			),
	);
};

const isReservedTag = (tag: string): boolean => {
	const productTags = [
		"FarmCam SIM",
		"FenceAlarm",
		"FenceAlarm2",
		"SmartPlug",
		"SmartPlug2",
	];
	if (productTags.includes(tag)) return true;

	// Check if email
	return /.+@.+\..+/.test(tag);
};

const selectedDeviceTagControl = (
	shared: Shared,
	tag: string,
): HTMLDivElement => {
	const onClickRemove = (): void => {
		shared.device.Metadata.Tags = sortedSetDelete(
			shared.device.Metadata.Tags,
			tag,
		);
	};

	return div(
		{ class: "control" },
		div(
			{ class: "tags has-addons" },
			span({ class: "tag is-primary" }, tag),
			span(
				{ class: "tag" },
				materialSymbol({
					id: "remove",
					onclick: onClickRemove,
				}),
			),
		),
	);
};

const unselectedDeviceTagControl = (
	shared: Shared,
	tag: string,
): HTMLDivElement => {
	const onClickAdd = (): void => {
		shared.device.Metadata.Tags = sortedSetAdd(
			shared.device.Metadata.Tags,
			tag,
		);
	};

	return div(
		{ class: "control" },
		div(
			{ class: "tags has-addons" },
			span({ class: "tag" }, tag),
			span(
				{ class: "tag" },
				materialSymbol({
					id: "add",
					onclick: onClickAdd,
				}),
			),
		),
	);
};
